<script lang="ts">
	import { twMerge } from 'tailwind-merge';
	import { Skeleton } from '../../../../../components/Skeleton';
	import { Txt } from '../../../../../components/Txt';
	import { getLocaleString } from '../../../../../utils/getLocaleString/getLocaleString';
	import { track } from '../../../../../utils/track/track';
	import { FilterCollection } from '../../../constants';
	import { filterStores } from '../../../state';
	import type { FilterSections } from '../../../types';
	import { getActiveArtistFromStores } from '../../../utils/getActiveArtist/getActiveArtist';
	import {
		FilterTrackEvent,
		getFilterTypeName
	} from '../../../utils/getFilterTrackEvents/getFilterTrackEvents';
	import { scrollToFilterResults } from '../../../utils/scrollToFilterResults';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';

	let {
		count,
		loading = false,
		suffix = '',
		filterSections = undefined,
		scrollTarget = undefined,
		hideChips = false,
		...rest
	}: Props = $props();

	interface Props {
		class?: string;
		count: number | null | undefined;
		loading?: boolean;
		// Appended to the number, e.g. '+' when the count is a lower bound
		// ("34+ results"). A suffixed count is never singular.
		suffix?: string;
		filterSections?: FilterSections;
		scrollTarget?: string;
		// Resolve the applied facets (for the zero-results event) without
		// rendering the chips, when another row already renders them.
		hideChips?: boolean;
	}

	interface AppliedFilterChip {
		clearAll?: boolean;
		filterId: string;
		label: string;
		value: string;
	}

	type LabelledFilterItem = {
		filter_checkboxes?: Array<{ label?: string | null; value?: string | null }> | null;
		filter_tags?: Array<{ label?: string | null; value?: string | null }> | null;
		label?: string | null;
	};

	const decodeValue = (value: string) => {
		try {
			return decodeURIComponent(value);
		} catch {
			return value;
		}
	};

	let chips = $derived.by(() => {
		if (!filterSections?.length) return [];

		return filterSections.flatMap((section) =>
			(section?.filter_type || []).flatMap((filterType) => {
				if (!filterType?.id) {
					return [];
				}

				const rawValue = page.url.searchParams.get(filterType.id);
				if (!rawValue) return [];

				const item = filterType.item as LabelledFilterItem;
				if (filterType.collection === FilterCollection.Filter_Toggle) {
					return [
						{
							clearAll: true,
							filterId: filterType.id,
							label: item.label || section?.title || 'Selected',
							value: decodeValue(rawValue)
						} satisfies AppliedFilterChip
					];
				}

				if (filterType.collection === FilterCollection.Filter_Slider) {
					const range = rawValue.split(',').map(decodeValue).join('–');
					return [
						{
							clearAll: true,
							filterId: filterType.id,
							label: `${item.label || section?.title || 'Range'}: ${range}`,
							value: rawValue
						} satisfies AppliedFilterChip
					];
				}

				if (
					![FilterCollection.Filter_Checkbox_List, FilterCollection.Filter_Tag_List].includes(
						filterType.collection as FilterCollection
					)
				) {
					return [];
				}

				const options = [...(item.filter_checkboxes || []), ...(item.filter_tags || [])];

				return rawValue.split(',').map((rawSelectedValue) => {
					const value = decodeValue(rawSelectedValue);
					const option = options.find((candidate) => String(candidate?.value) === value);

					return {
						filterId: filterType.id as string,
						label: option?.label || value,
						value
					} satisfies AppliedFilterChip;
				});
			})
		);
	});

	// `chips` stays resolved for the zero-results event even when this row is not
	// the one rendering them.
	let visibleChips = $derived(hideChips ? [] : chips);

	const { trackPrefixStore } = filterStores;

	// A filter combination that returns nothing is a demand signal (what people
	// look for but we don't stock), so it is tracked as its own event.
	//
	// Async grids derive `count` from an in-flight query whose `|| 0` fallback
	// makes it a literal 0 before results land, so a bare `count === 0` check
	// would fire on every filter change. `loading` is what distinguishes
	// "no results" from "no results yet" — never report while it is true.
	//
	// Keyed by URL + count so a genuine zero is reported once per filter
	// combination, not on every unrelated re-render of the same result set.
	let lastTrackedNoResultsKey: string | undefined = $state();

	$effect(() => {
		const trackPrefix = $trackPrefixStore;

		if (!trackPrefix || loading || count !== 0) return;

		// Nothing applied means an empty catalogue, not an unmet search.
		if (!chips.length) return;

		const key = `${page.url.search}|${count}`;
		if (lastTrackedNoResultsKey === key) return;

		lastTrackedNoResultsKey = key;

		const artist = getActiveArtistFromStores();

		track(`${trackPrefix}/${FilterTrackEvent.NoResults}`, {
			filter_count: chips.length,
			filter_type: chips
				.map(({ filterId }) =>
					getFilterTypeName({
						filterSections: filterSections || [],
						filterCollectionId: filterId
					})
				)
				.join(', '),
			filter_value: chips.map(({ label }) => label).join(', '),
			...(artist ? { artist } : {})
		});
	});

	const removeChip = async (chip: AppliedFilterChip) => {
		const url = new URL(page.url.href);
		const remainingValues = (url.searchParams.get(chip.filterId) || '')
			.split(',')
			.map(decodeValue)
			.filter((value) => value !== chip.value);

		if (chip.clearAll) {
			url.searchParams.delete(chip.filterId);
			url.searchParams.delete(`${chip.filterId}_type`);
			url.searchParams.delete(`${chip.filterId}_inclusive`);
		} else if (remainingValues.length) {
			url.searchParams.set(
				chip.filterId,
				remainingValues.map((value) => encodeURIComponent(value)).join(',')
			);
		} else {
			url.searchParams.delete(chip.filterId);
			url.searchParams.delete(`${chip.filterId}_type`);
			url.searchParams.delete(`${chip.filterId}_inclusive`);
		}

		// Chip removal navigates directly rather than via applyFilters, so it
		// needs its own tracking. Fire-and-forget: never awaited. The label is
		// reported (not the raw value) to match FILTER_APPLIED's filter_value.
		if ($trackPrefixStore) {
			const artist = getActiveArtistFromStores();

			track(`${$trackPrefixStore}/${FilterTrackEvent.FilterCleared}`, {
				filter_type: getFilterTypeName({
					filterSections: filterSections || [],
					filterCollectionId: chip.filterId
				}),
				filter_value: chip.label,
				...(artist ? { artist } : {})
			});
		}

		// `reset: false` keeps both the scroll position and the focus.
		await goto(url, { reset: false });

		scrollToFilterResults(scrollTarget);
	};

	let text = $derived(
		count === 1 && !suffix ? `${count} result` : `${getLocaleString(count || 0)}${suffix} results`
	);
</script>

{#if loading}
	<div
		class={twMerge(
			'flex flex-wrap items-center justify-between gap-3',
			visibleChips.length ? 'pb-3' : '',
			rest.class
		)}
	>
		<Skeleton variant="text" class="ml-auto h-[16px] w-[60px]" />
	</div>
{:else if typeof count === 'number' || visibleChips.length}
	<div
		class={twMerge(
			'flex flex-wrap items-center justify-between gap-3',
			visibleChips.length ? 'pb-3' : '',
			rest.class
		)}
	>
		{#if visibleChips.length}
			<!-- Cap at 3 chip rows (3 × 1.75rem chip + 2 × 0.5rem gap = 6.25rem), scrolling
			     internally past that: this row can sit inside a sticky toolbar, and with many
			     chips an uncapped block grows taller than a phone viewport and pins over the
			     results. Keep the cap in sync with the chip's py-1.5 + body3 line-height. -->
			<div
				class="flex max-h-[6.25rem] flex-wrap gap-2 overflow-y-auto"
				role="group"
				aria-label="Applied filters"
			>
				{#each visibleChips as chip (`${chip.filterId}-${chip.value}`)}
					<button
						type="button"
						class="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-1.5 text-gray-800 hover:bg-gray-200"
						aria-label={`Remove ${chip.label} filter`}
						onclick={() => removeChip(chip)}
					>
						<Txt variant="body3">{chip.label}</Txt>
						<span aria-hidden="true" class="text-base leading-none">×</span>
					</button>
				{/each}
			</div>
		{/if}
		{#if typeof count === 'number'}
			<Txt dataCy="filter-results" class="ml-auto text-gray-500" variant="body3">{text}</Txt>
		{/if}
	</div>
{/if}
